import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Mail, Plus, Trash2, Save, Play, Pause,
    Clock, ChevronDown, ChevronUp, Eye, Edit,
    CheckCircle, Send, Settings, Zap
} from 'lucide-react';
import {
    emailApi,
    type EmailFlow,
    type EmailFlowStep,
    type EmailTemplate,
    type EventType
} from '../../lib/api/email';
import { EmailEditorModal } from '../../components/email';

// ==================== SUB COMPONENTS ====================

interface StepCardProps {
    step: EmailFlowStep;
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onDelete: (() => void) | undefined;
    onMoveUp: () => void;
    onMoveDown: () => void;
    isFirst: boolean;
    isLast: boolean;
}

const StepCard: React.FC<StepCardProps> = ({
    step, index, isSelected, onClick, onDelete, onMoveUp, onMoveDown, isFirst, isLast
}) => {
    const delayText = step.delayAmount > 0
        ? `After ${step.delayAmount} ${step.delayUnit}`
        : 'Immediately';

    return (
        <div className="relative">
            {/* Connector Line */}
            {index > 0 && (
                <div className="absolute left-1/2 -top-6 w-px h-6 bg-neutral-300" />
            )}
            {/* Delay Label */}
            {index > 0 && (
                <div className="absolute left-1/2 -top-4 transform -translate-x-1/2 bg-white px-2 text-xs text-neutral-500 flex items-center gap-1">
                    <Clock size={10} />
                    {delayText}
                </div>
            )}

            <div
                onClick={onClick}
                className={`relative bg-white border-2 rounded-xl p-4 cursor-pointer transition-all ${isSelected
                    ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                    : 'border-neutral-200 hover:border-neutral-300'
                    }`}
            >
                {/* Step Number Badge */}
                <div className="absolute -top-3 -left-3 w-6 h-6 bg-neutral-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                </div>

                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{step.name}</div>
                        <div className="text-xs text-neutral-500 mt-0.5 truncate">{step.subject}</div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                            disabled={isFirst}
                            className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"
                        >
                            <ChevronUp size={14} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                            disabled={isLast}
                            className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"
                        >
                            <ChevronDown size={14} />
                        </button>
                        {onDelete && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                className="p-1 hover:bg-red-100 rounded text-red-500"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                    <span className="flex items-center gap-1">
                        <Send size={10} />
                        {step.sentCount} sent
                    </span>
                    <span className="flex items-center gap-1">
                        <Eye size={10} />
                        {step.sentCount > 0 ? ((step.openCount / step.sentCount) * 100).toFixed(0) : 0}% opens
                    </span>
                    <span className="flex items-center gap-1">
                        <CheckCircle size={10} />
                        {step.sentCount > 0 ? ((step.clickCount / step.sentCount) * 100).toFixed(0) : 0}% clicks
                    </span>
                </div>
            </div>
        </div>
    );
};

// ==================== MAIN COMPONENT ====================

export const EmailFlowDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [flow, setFlow] = useState<EmailFlow | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);

    // Editor state
    const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
    const [hasChanges, setHasChanges] = useState(false);

    // New step form
    const [showAddStep, setShowAddStep] = useState(false);
    const [newStepName, setNewStepName] = useState('');
    const [newStepSubject, setNewStepSubject] = useState('');
    const [newStepDelay, setNewStepDelay] = useState(0);
    const [newStepDelayUnit, setNewStepDelayUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
    const [newStepContent, setNewStepContent] = useState('');

    // Edit step form
    const [editStepName, setEditStepName] = useState('');
    const [editStepSubject, setEditStepSubject] = useState('');
    const [editStepDelay, setEditStepDelay] = useState(0);
    const [editStepDelayUnit, setEditStepDelayUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
    const [editStepContent, setEditStepContent] = useState('');

    // Email editor modal
    const [showEmailEditor, setShowEmailEditor] = useState(false);


    // Flow settings
    const [flowName, setFlowName] = useState('');
    const [flowDescription, setFlowDescription] = useState('');
    const [flowTrigger, setFlowTrigger] = useState('');

    const fetchFlow = useCallback(async () => {
        if (!id) return;
        try {
            const [flowData, templatesData, eventTypesData] = await Promise.all([
                emailApi.getFlow(id),
                emailApi.listTemplates().catch(err => {
                    console.error('Failed to fetch templates:', err);
                    return [];
                }),
                emailApi.getEventTypes().catch(err => {
                    console.error('Failed to fetch event types:', err);
                    return [];
                })
            ]);

            setFlow(flowData);
            setTemplates(templatesData);
            setEventTypes(eventTypesData);

            // Initialize form values
            setFlowName(flowData.name);
            setFlowDescription(flowData.description || '');
            setFlowTrigger(flowData.triggerType);
        } catch (error) {
            console.error('Failed to fetch flow:', error);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchFlow();
    }, [fetchFlow]);

    useEffect(() => {
        if (selectedStepIndex !== null && flow?.steps[selectedStepIndex]) {
            const step = flow.steps[selectedStepIndex];
            setEditStepName(step.name);
            setEditStepSubject(step.subject);
            setEditStepDelay(step.delayAmount);
            setEditStepDelayUnit(step.delayUnit);
            setEditStepContent(step.htmlContent || '');
        }
    }, [selectedStepIndex, flow]);

    const handleSaveFlow = async () => {
        if (!flow) return;
        setSaving(true);
        try {
            await emailApi.updateFlow(flow.id, {
                name: flowName,
                description: flowDescription || undefined,
                triggerType: flowTrigger
            });
            setHasChanges(false);
            fetchFlow();
        } catch (error) {
            console.error('Failed to save flow:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async () => {
        if (!flow) return;
        try {
            if (flow.status === 'active') {
                await emailApi.pauseFlow(flow.id);
            } else {
                await emailApi.activateFlow(flow.id);
            }
            fetchFlow();
        } catch (error) {
            console.error('Failed to toggle flow status:', error);
        }
    };

    const handleAddStep = async () => {
        if (!flow || !newStepName || !newStepSubject) return;
        try {
            await emailApi.addStep(flow.id, {
                name: newStepName,
                subject: newStepSubject,
                delayAmount: newStepDelay,
                delayUnit: newStepDelayUnit,
                htmlContent: newStepContent || undefined
            });
            setShowAddStep(false);
            setNewStepName('');
            setNewStepSubject('');
            setNewStepDelay(0);
            setNewStepDelayUnit('hours');
            setNewStepContent('');
            fetchFlow();
        } catch (error) {
            console.error('Failed to add step:', error);
        }
    };

    const handleUpdateStep = async () => {
        if (!flow || selectedStepIndex === null) return;
        const step = flow.steps[selectedStepIndex];
        try {
            await emailApi.updateStep(flow.id, step.id, {
                name: editStepName,
                subject: editStepSubject,
                delayAmount: editStepDelay,
                delayUnit: editStepDelayUnit,
                htmlContent: editStepContent || undefined,
                templateSlug: undefined // Always unlink template when saving custom content
            });
            fetchFlow();
        } catch (error) {
            console.error('Failed to update step:', error);
        }
    };

    const handleDeleteStep = async (stepId: string) => {
        if (!flow) return;
        if (!confirm('Are you sure you want to delete this step?')) return;
        try {
            await emailApi.deleteStep(flow.id, stepId);
            setSelectedStepIndex(null);
            fetchFlow();
        } catch (error) {
            console.error('Failed to delete step:', error);
        }
    };

    const handleMoveStep = async (fromIndex: number, direction: 'up' | 'down') => {
        if (!flow) return;
        const steps = [...flow.steps];
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        if (toIndex < 0 || toIndex >= steps.length) return;

        // Swap
        [steps[fromIndex], steps[toIndex]] = [steps[toIndex], steps[fromIndex]];
        const stepIds = steps.map(s => s.id);

        try {
            await emailApi.reorderSteps(flow.id, stepIds);
            fetchFlow();
        } catch (error) {
            console.error('Failed to reorder steps:', error);
        }
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-neutral-200 border-t-neutral-900 rounded-full" />
            </div>
        );
    }

    if (!flow) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-neutral-500">
                <Mail size={48} className="mb-4" />
                <p>Flow not found</p>
                <button
                    onClick={() => navigate('/email')}
                    className="mt-4 px-4 py-2 text-sm text-blue-600 hover:underline"
                >
                    Back to Email Manager
                </button>
            </div>
        );
    }

    const selectedStep = selectedStepIndex !== null ? flow.steps[selectedStepIndex] : null;

    return (
        <div className="h-full flex flex-col p-1">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/email')}
                            className="p-2 hover:bg-neutral-100 rounded-lg"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div>
                            <h1 className="text-lg font-semibold text-neutral-900">{flow.name}</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${flow.status === 'active'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : flow.status === 'paused'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-neutral-100 text-neutral-600'
                                    }`}>
                                    {flow.status.charAt(0).toUpperCase() + flow.status.slice(1)}
                                </span>
                                <span className="text-xs text-neutral-500">
                                    {flow.steps.length} steps • {flow.totalSent} sent
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleToggleStatus}
                            className={`px-3 py-2 text-xs rounded-lg flex items-center gap-1 ${flow.status === 'active'
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                }`}
                        >
                            {flow.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                            {flow.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                        <button
                            onClick={handleSaveFlow}
                            disabled={!hasChanges || saving}
                            className="px-3 py-2 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-1 disabled:opacity-50"
                        >
                            <Save size={12} />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Panel - Flow Builder */}
                    <div className="flex-1 overflow-y-auto p-6 bg-neutral-50">
                        <div className="max-w-lg mx-auto">
                            {/* Trigger */}
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-4 mb-8 shadow-lg">
                                <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wide mb-1">
                                    <Zap size={12} />
                                    Trigger
                                </div>
                                <div className="font-medium">
                                    {eventTypes.find(e => e.type === flow.triggerType)?.label || flow.triggerType}
                                </div>
                            </div>

                            {/* Steps */}
                            <div className="space-y-8">
                                {flow.steps.map((step, index) => (
                                    <StepCard
                                        key={step.id}
                                        step={step}
                                        index={index}
                                        isSelected={selectedStepIndex === index}
                                        onClick={() => setSelectedStepIndex(index)}
                                        onDelete={flow.isSystem ? undefined : () => handleDeleteStep(step.id)}
                                        onMoveUp={() => handleMoveStep(index, 'up')}
                                        onMoveDown={() => handleMoveStep(index, 'down')}
                                        isFirst={index === 0}
                                        isLast={index === flow.steps.length - 1}
                                    />
                                ))}
                            </div>

                            {/* Add Step Button */}
                            <div className="mt-8">
                                {flow.steps.length > 0 && (
                                    <div className="flex justify-center mb-4">
                                        <div className="w-px h-6 bg-neutral-300" />
                                    </div>
                                )}
                                {!flow.isSystem && (
                                    <button
                                        onClick={() => setShowAddStep(true)}
                                        className="w-full py-3 border-2 border-dashed border-neutral-300 rounded-xl text-neutral-500 hover:border-neutral-400 hover:text-neutral-600 flex items-center justify-center gap-2 transition-colors"
                                    >
                                        <Plus size={18} />
                                        Add Email Step
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Step Editor / Flow Settings */}
                    <div className="w-96 border-l border-neutral-200 overflow-y-auto p-4">
                        {selectedStep ? (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-neutral-900">Edit Step</h3>
                                    <button
                                        onClick={() => setSelectedStepIndex(null)}
                                        className="text-xs text-neutral-500 hover:text-neutral-700"
                                    >
                                        Close
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Step Name</label>
                                        <input
                                            type="text"
                                            value={editStepName}
                                            onChange={(e) => setEditStepName(e.target.value)}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Subject Line</label>
                                        <input
                                            type="text"
                                            value={editStepSubject}
                                            onChange={(e) => setEditStepSubject(e.target.value)}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Delay</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={editStepDelay}
                                                onChange={(e) => setEditStepDelay(parseInt(e.target.value) || 0)}
                                                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Unit</label>
                                            <select
                                                value={editStepDelayUnit}
                                                onChange={(e) => setEditStepDelayUnit(e.target.value as any)}
                                                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                            >
                                                <option value="minutes">Minutes</option>
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Load from Template</label>
                                        <select
                                            value=""
                                            onChange={(e) => {
                                                const slug = e.target.value;
                                                const template = templates.find(t => t.slug === slug);
                                                if (template) {
                                                    if (confirm('This will replace current content with the selected template. Continue?')) {
                                                        setEditStepContent(template.htmlContent);
                                                        setEditStepSubject(template.subject);
                                                    }
                                                }
                                            }}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        >
                                            <option value="">Select a template to copy...</option>
                                            {templates.map(t => (
                                                <option key={t.slug} value={t.slug}>{t.name}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-neutral-500 mt-1">Selecting a template will copy its content to this step.</p>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Email Content (HTML)</label>
                                            <button
                                                onClick={() => setShowEmailEditor(true)}
                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                            >
                                                <Edit size={12} />
                                                Edit
                                            </button>
                                        </div>
                                        <textarea
                                            value={editStepContent}
                                            onChange={(e) => setEditStepContent(e.target.value)}
                                            rows={12}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                                        />
                                    </div>

                                    {/* Email Editor Modal */}
                                    <EmailEditorModal
                                        isOpen={showEmailEditor}
                                        onClose={() => setShowEmailEditor(false)}
                                        initialContent={editStepContent}
                                        subject={editStepSubject}
                                        onSave={(htmlContent) => setEditStepContent(htmlContent)}
                                    />

                                    <button
                                        onClick={handleUpdateStep}
                                        className="w-full py-2.5 bg-neutral-900 text-white rounded-lg text-sm hover:bg-neutral-800 flex items-center justify-center gap-2"
                                    >
                                        <Save size={14} />
                                        Save Step
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                                    <Settings size={16} />
                                    Flow Settings
                                </h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Flow Name</label>
                                        <input
                                            type="text"
                                            value={flowName}
                                            onChange={(e) => { setFlowName(e.target.value); setHasChanges(true); }}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Description</label>
                                        <textarea
                                            value={flowDescription}
                                            onChange={(e) => { setFlowDescription(e.target.value); setHasChanges(true); }}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Trigger Event</label>
                                        <select
                                            value={flowTrigger}
                                            disabled={flow.isSystem}
                                            onChange={(e) => { setFlowTrigger(e.target.value); setHasChanges(true); }}
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none disabled:bg-neutral-100 disabled:text-neutral-500"
                                        >
                                            {eventTypes.map(et => (
                                                <option key={et.type} value={et.type}>{et.label}</option>
                                            ))}
                                        </select>
                                        {flow.isSystem && (
                                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                                <Settings size={10} />
                                                System flow triggers cannot be changed
                                            </p>
                                        )}
                                    </div>

                                    <div className="pt-4 border-t border-neutral-200">
                                        <h4 className="text-xs font-bold text-neutral-900 uppercase tracking-wide mb-3">Flow Statistics</h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-neutral-50 rounded-lg p-3">
                                                <div className="text-lg font-bold text-neutral-900">{flow.totalSent}</div>
                                                <div className="text-xs text-neutral-500">Total Sent</div>
                                            </div>
                                            <div className="bg-neutral-50 rounded-lg p-3">
                                                <div className="text-lg font-bold text-neutral-900">{flow.openRate}%</div>
                                                <div className="text-xs text-neutral-500">Open Rate</div>
                                            </div>
                                            <div className="bg-neutral-50 rounded-lg p-3">
                                                <div className="text-lg font-bold text-neutral-900">{flow.totalOpened}</div>
                                                <div className="text-xs text-neutral-500">Opened</div>
                                            </div>
                                            <div className="bg-neutral-50 rounded-lg p-3">
                                                <div className="text-lg font-bold text-neutral-900">{flow.clickRate}%</div>
                                                <div className="text-xs text-neutral-500">Click Rate</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Step Modal */}
            {
                showAddStep && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Add Email Step</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Step Name</label>
                                    <input
                                        type="text"
                                        value={newStepName}
                                        onChange={(e) => setNewStepName(e.target.value)}
                                        placeholder="e.g., Welcome Email"
                                        className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Subject Line</label>
                                    <input
                                        type="text"
                                        value={newStepSubject}
                                        onChange={(e) => setNewStepSubject(e.target.value)}
                                        placeholder="e.g., Welcome to Dashbal!"
                                        className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Delay After Previous</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={newStepDelay}
                                            onChange={(e) => setNewStepDelay(parseInt(e.target.value) || 0)}
                                            className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Unit</label>
                                        <select
                                            value={newStepDelayUnit}
                                            onChange={(e) => setNewStepDelayUnit(e.target.value as any)}
                                            className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                        >
                                            <option value="minutes">Minutes</option>
                                            <option value="hours">Hours</option>
                                            <option value="days">Days</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Email Content (HTML)</label>
                                    <textarea
                                        value={newStepContent}
                                        onChange={(e) => setNewStepContent(e.target.value)}
                                        placeholder="<div>Your email content here...</div>"
                                        rows={4}
                                        className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowAddStep(false)}
                                    className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddStep}
                                    disabled={!newStepName || !newStepSubject}
                                    className="px-4 py-2 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                                >
                                    Add Step
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};
